/**
 * Escalation Repository
 */

import { BaseRepository } from './BaseRepository';
import { Escalation } from '../types/WorkflowEntities';

class EscalationRepository extends BaseRepository<Escalation> {
    protected entityName = 'escalation';
    protected graphQLType = 'Escalation';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Escalation | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Escalation',
            criticalityLevel: 'CRITICAL', // Escalations are critical for incident response
            metadata: { id, escalationRepository: true }
        });

        const query = `
            query GetEscalationByTenantAndId($id: String!, $tenant: String!) {
                escalation(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ escalation: Escalation | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 60 * 1000 // 1 minute cache for escalations (must be fresh)
            });

            const escalation = result.escalation || null;

            // ✅ FERP SAFETY: Log successful operation with escalation details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Escalation',
                    entityId: id,
                    found: !!escalation,
                    escalationEnabled: escalation?.enabled,
                    escalationScope: escalation?.scope,
                    escalationLevels: escalation?.levels?.length,
                    timestamp: Date.now()
                });
            }

            return escalation;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Escalation',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(escalationId: string): Promise<Escalation | null> {
        return this.findByTenantAndId(this.tenant, escalationId);
    }

    // Escalation-specific methods
    async trigger(escalationId: string, context: any): Promise<any> {
        const mutation = `
            mutation TriggerEscalation($escalationId: ID!, $context: JSON!) {
                triggerEscalation(escalationId: $escalationId, context: $context) {
                    success
                    escalationInstanceId
                    nextLevel
                    notificationsSent
                }
            }
        `;

        return this.executeQuery(mutation, { escalationId, context });
    }

    async findByScope(scope: string): Promise<Escalation[]> {
        const query = `
            query FindEscalationsByScope($scope: String!) {
                escalations(filter: { scope: $scope }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { scope });
        return result.escalations || [];
    }

    async getActive(): Promise<any[]> {
        const query = `
            query GetActiveEscalations {
                activeEscalations {
                    id
                    escalationId
                    currentLevel
                    nextEscalation
                    context
                    createdAt
                }
            }
        `;

        const result = await this.executeQuery(query, {});
        return result.activeEscalations || [];
    }

    async cancel(escalationInstanceId: string): Promise<any> {
        const mutation = `
            mutation CancelEscalation($escalationInstanceId: ID!) {
                cancelEscalation(escalationInstanceId: $escalationInstanceId) {
                    success
                    message
                }
            }
        `;

        return this.executeQuery(mutation, { escalationInstanceId });
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            description
            triggers {
                type
                condition
                threshold
                timeframe
            }
            levels {
                level
                delay
                recipients {
                    type
                    identifier
                    notificationMethod
                }
                actions {
                    type
                    config
                }
            }
            enabled
            scope
            createdAt
            updatedAt
        `;
    }
}

export const escalationRepository = new EscalationRepository();
export { EscalationRepository };